import { css } from 'aphrodite';
import { FC, FormEvent, useState } from 'react';
import { AIPromptUsage, IAIPromptTemplate } from '../../../../models/AIPromptTemplate';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useAIPromptLibrary } from '../../../../queries/AIPrompt';
import { useAIPromptField } from '../../../../shared/components/ai/AIPromptField/useAIPromptField';
import { Button } from '../../../components-v2/Button';
import { InputGroupAddon, InputGroupButton, InputGroupTextarea, InputGroupV2 } from '../../../components-v2/InputGroup';
import { PaperPlaneIcon } from '../../svgs/icons/PaperPlaneIcon';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { styleSheet } from './styles';

const TELEMETRY_CATEGORY = 'DashboardAskBar';
// Ids of the prompt library's Ask Lev samples, seeded by AIPromptSurfaceLibrary in Levitate-API.
const STARTER_PROMPT_IDS = [
	'sample:ask-my-tasks',
	'sample:ask-catch-up-contact',
	'sample:ask-draft-follow-up',
	'sample:ask-how-do-i',
];

export const DashboardAskBar: FC<{ onAsk: (text: string) => void }> = ({ onAsk }) => {
	const userSession = useUserSession();
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	const [value, setValue] = useState('');
	const [starterId, setStarterId] = useState<string | null>(null);
	const field = useAIPromptField({ onChange: setValue, value });
	const libraryQuery = useAIPromptLibrary({
		industry: userSession.account?.additionalInfo?.industry,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'DashboardAskBarLoadStarters-Error' } },
		usage: AIPromptUsage.AssistantChat,
	});
	const starters = (libraryQuery.data ?? [])
		.filter(prompt => STARTER_PROMPT_IDS.includes(prompt.id))
		.sort((a, b) => STARTER_PROMPT_IDS.indexOf(a.id) - STARTER_PROMPT_IDS.indexOf(b.id));
	const canSend = !!value.trim() && !field.unfilledPlaceholder;

	const applyStarter = (prompt: IAIPromptTemplate) => {
		logEvent('StarterClicked', { promptId: prompt.id });
		setStarterId(prompt.id);
		field.applyPrompt(prompt);
	};

	const send = (event: FormEvent) => {
		event.preventDefault();
		if (!canSend) {
			return;
		}
		logEvent('Sent', { source: starterId ? 'starter' : 'typed', promptId: starterId });
		onAsk(value.trim());
		setValue('');
		setStarterId(null);
	};

	return (
		<div className={css(styleSheet.root)}>
			<form onSubmit={send}>
				<InputGroupV2>
					<InputGroupAddon>
						<SparklesIcon className={css(styleSheet.askIcon)} />
					</InputGroupAddon>
					<InputGroupTextarea
						ref={field.ref}
						rows={1}
						styles={[styleSheet.textarea]}
						aria-label='Ask Lev'
						placeholder='Ask Lev about your clients, tasks or emails'
						value={value}
						onChange={event => {
							setValue(event.target.value);
							if (!event.target.value) {
								setStarterId(null);
							}
						}}
						onKeyDown={event => {
							if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
								event.preventDefault();
								event.currentTarget.form?.requestSubmit();
							}
						}}
					/>
					<InputGroupAddon align='inlineEnd'>
						<InputGroupButton
							type='submit'
							variant='primary'
							size='iconXs'
							aria-label='Send to Lev'
							disabled={!canSend}
						>
							<PaperPlaneIcon />
						</InputGroupButton>
					</InputGroupAddon>
				</InputGroupV2>
			</form>
			{starters.length > 0 ? (
				<div className={css(styleSheet.starters)}>
					{starters.map(prompt => (
						<Button key={prompt.id} type='button' variant='secondary' size='sm' onClick={() => applyStarter(prompt)}>
							{prompt.name}
						</Button>
					))}
				</div>
			) : null}
		</div>
	);
};
